import React, { FC, useState } from 'react';
import dayjs from 'dayjs';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { FundTypeEnum, GoldTypeEnum, SilverTypeEnum, TransactionActionEnum } from '~utils/enums';

import SummaryPDF from './SummaryPDF';
import * as SummaryStyles from './_buyOrSellGoldSummary.module.scss';
import { TBuyOrSellMultipleProductsParams } from '~types/feat/gold';

type BuyOrSellGoldSummaryProps = {
    transactionType: TransactionActionEnum;
}
const BuyOrSellGoldSummary: FC<BuyOrSellGoldSummaryProps> = ({
    transactionType,
}) => {
    const dispatch = useAppDispatch();

    const buyOrSellGoldParams = useAppSelector((state) => state.gold.params.buyOrSellGoldParams);

    const createBuyGoldTransactionAttempt = useAppSelector((state) => state.gold.loadings.createBuyGoldTransaction);
    const createSellGoldTransactionAttempt = useAppSelector((state) => state.gold.loadings.createSellGoldTransaction);

    const discountList = useAppSelector((state) => state.setting.allDiscounts);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const createBuyGoldTransaction = () => {
        dispatch(combinedActions.goldCreateBuyGoldTransactionAttempt());
    };

    const createSellGoldTransaction = () => {
        dispatch(combinedActions.goldCreateSellGoldTransactionAttempt());
    };

    const totalQuantity = (item: TBuyOrSellMultipleProductsParams) => {
        if (item.goldType === GoldTypeEnum.PaperGold || item.silverType === SilverTypeEnum.PaperSilver) {
            return `${Number(item.amount).toFixed(3)} g`;
        }

        return `${Number(item.amount)} pcs`;
    };

    const handleViewSerialNo = (serialNo: string) => {
        setIsSerialNoModalOpen(true);

        if (!serialNo) return;

        setSerialNoList(serialNo.split(','));
    };

    const renderSerialNo = (item: TBuyOrSellMultipleProductsParams) => {
        if (item.goldType === GoldTypeEnum.PaperGold || item.silverType === SilverTypeEnum.PaperSilver) return ': N/A';
        if (!item.serialNo) return ': N/A';

        return (
            <a
                className={SummaryStyles['show-serial-no']}
                onClick={() => handleViewSerialNo(item.serialNo)}
            >
                : Show all...
            </a>
        );
    };

    const getDiscountAmount = (discountId: number) => {
        const discount = discountList.find((item) => item.discountId === discountId);

        if (!discount) return '';

        if (discount.amount) return `RM${((discount.amount * 100) / 100).toFixed(2)}`;

        if (discount.percentageAmount) return `${discount.percentageAmount}%`;

        return '';
    };

    const getDiscountName = (discountId: number) => {
        const discount = discountList.find((item) => item.discountId === discountId);

        if (!discount) return '';

        return discount.discountName;
    };

    const renderRegisteredCategory = () => {
        let registeredCategoryArray: string[] = [];

        buyOrSellGoldParams.products.map( (product) => {
            const registeredCategory = `${FundTypeEnum[buyOrSellGoldParams.fundType]} | ${GoldTypeEnum[product.goldType]?.replace('Gold', ' Gold') || SilverTypeEnum[product.silverType]?.replace('Silver', ' Silver')}`;

            if (registeredCategoryArray.includes(registeredCategory)) return;

            registeredCategoryArray = [...registeredCategoryArray, registeredCategory];
        });

        return registeredCategoryArray;
    };

    const getTotalNetValue = () => {
        let totalNetValue = 0;

        buyOrSellGoldParams.products.map( (product) => {
            totalNetValue += Number((Math.round(Number(product.totalNetValue) * 100) / 100).toFixed(2));
        });

        return (Math.round(totalNetValue * 100) / 100).toFixed(2);
    };

    const itemsSummary = buyOrSellGoldParams.products.flatMap((item, index) => ([
        { label: `Item ${index + 1}` },
        { label: 'Product Name', value: item.productName },
        { label: 'Quantity', value: totalQuantity(item) },
        { label: 'Price per gram (MYR)', value: item.goldAmountMYR },
        { label: 'Serial No.', value: renderSerialNo(item) },
        { label: 'Discount Name', value: getDiscountName(item.discountId) },
        { label: 'Discount (MYR/%)', value: getDiscountAmount(item.discountId) },
    ]));

    const summaryTable = [
        {
            label: 'Transaction Status',
            value: 'Pending',
        },
        {
            label: 'Transaction Date',
            value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Earmark End Date',
            value: buyOrSellGoldParams.earmarkEndDate ? dayjs(buyOrSellGoldParams.earmarkEndDate).format('DD/MM/YYYY hh:mm A') : '-',
        },
        {
            label: 'Earmark Transaction',
            value: buyOrSellGoldParams.isEarmarkTransaction ? 'Yes' : '-',
        },
        {
            label: 'Project Name',
            value: buyOrSellGoldParams.projectName ? buyOrSellGoldParams.projectName : '-',
        },
        {
            label: 'Region Name',
            value: buyOrSellGoldParams.region,
        },
        {
            label: 'Branch Name',
            value: buyOrSellGoldParams.branchName,
        },
        {
            label: 'Branch Code',
            value: buyOrSellGoldParams.branchCode,
        },
        {
            label: 'Register Category',
            value: renderRegisteredCategory(),
        },
        {
            label: 'Name of Customer/Company Firm/Association',
            value: buyOrSellGoldParams.name,
        },
        {
            label: 'Identification No.',
            value: buyOrSellGoldParams.idNo,
        },
        {
            label: 'CIF No.',
            value: buyOrSellGoldParams.CIFNo,
        },
        ...itemsSummary,
        {
            label: 'Total Amount (MYR)',
            value: getTotalNetValue(),
        },
    ];

    const productTableHeader = ['Gold Product', 'Price per gram (MYR)'];
    const productTableData = buyOrSellGoldParams.products.map((product) => {
        return { productName: product.productName, amount: product.amount, goldAmountMYR: product.goldAmountMYR, goldType: product.goldType, silverType: product.silverType };
    });

    const handleSubmitForApproval = () => {
        if (transactionType === TransactionActionEnum['Bank Sell']) {
            createBuyGoldTransaction();
        } else if (transactionType === TransactionActionEnum['Bank Buy']) {
            createSellGoldTransaction();
        }
    };

    return (
        <div className={SummaryStyles.container}>
            <table className={SummaryStyles['summary-table']}>
                <tbody>
                    {summaryTable.map((item, index) => {
                        return (
                            <tr key={index}>
                                <td>{item.label}</td>
                                <td>{(item.label === 'Serial No.' ? item.value : `${item.value === undefined ? '' : `: ${item.value}`}`)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className={SummaryStyles['product-container']}>
                <Text
                    label='Gold Description'
                    isBold
                />

                <table>
                    <tbody>
                        <tr>
                            {productTableHeader.map((item, index) => (
                                <td key={index}>
                                    {item}
                                </td>
                            ))}
                        </tr>

                        {productTableData.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    {item.productName}
                                </td>

                                <td>
                                    {item.goldAmountMYR}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={SummaryStyles['button-container']}>
                <SummaryPDF transactionType={transactionType} />

                <ActionButton
                    label='Back to Form'
                    onClick={() => dispatch(combinedActions.goldSetIsFormFilled(false))}
                />

                <ActionButton
                    label='Submit for Approval'
                    onClick={handleSubmitForApproval}
                    isLoading={transactionType === TransactionActionEnum['Bank Sell'] ? createBuyGoldTransactionAttempt : createSellGoldTransactionAttempt}
                />
            </div>

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </div>
    );
};

export default BuyOrSellGoldSummary;
